import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { IonSpinner } from '@ionic/react';
import { Helmet } from 'react-helmet-async';
import FullScreenRoute from '../../hoc/FullScreenRoute';
import ExecutionDetailsPage from './ExecutionDetailsPage';
import api, { accountApi } from '../../services/api';

const ExecutionDetailsPageWrapper = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [collection, setCollection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [executionName, setExecutionName] = useState(null);
  const [executionCollectionId, setExecutionCollectionId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const projectData = await api.get(`/projects/${projectId}`);
      setProject(projectData.project);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch collection when executionCollectionId is set by child component
  useEffect(() => {
    if (!executionCollectionId || !projectId) return;
    
    const fetchCollection = async () => {
      try {
        const collectionsData = await accountApi.get(`/collections?projectId=${projectId}`);
        const collections = collectionsData.collections || [];
        const executionCollection = collections.find(c => c.collectionId === executionCollectionId);
        if (executionCollection) {
          setCollection(executionCollection);
        }
      } catch (error) {
        console.error('Error fetching collection:', error);
      }
    };
    
    fetchCollection();
  }, [executionCollectionId, projectId]);

  if (loading) {
    return (
      <FullScreenRoute breadcrumbs={[]}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <IonSpinner />
        </div>
      </FullScreenRoute>
    );
  }

  // Build breadcrumbs with proper structure for dropdowns
  const breadcrumbs = [
    { label: 'Projects', path: '/projects' },
    { 
      label: project?.name || 'Project', 
      path: `/projects/${projectId}`,
      projectId: projectId  // This enables collections dropdown
    }
  ];
  
  // Add collection breadcrumb if it exists
  if (executionCollectionId && collection) {
    breadcrumbs.push({
      label: collection.collectionName || collection.name || 'Collection',
      isCollectionWithExecutions: true,  // This enables executions dropdown
      collectionId: executionCollectionId
    });
  }
  
  // Add execution name as final breadcrumb
  breadcrumbs.push({ label: executionName || 'Execution' });

  return (
    <>
      <Helmet>
        <title>{executionName ? `${executionName} - Teamy` : 'Execution Details - Teamy'}</title>
        <meta name="description" content="View execution details and test results in Teamy." />
      </Helmet>
      <FullScreenRoute breadcrumbs={breadcrumbs}>
        <ExecutionDetailsPage 
          setExecutionName={setExecutionName}
          setExecutionCollectionId={setExecutionCollectionId}
        />
      </FullScreenRoute>
    </>
  );
};

export default ExecutionDetailsPageWrapper;

